// Obuna qo'shimchalari nomi (Q-079): ega paneli va konsolda bir xil.
// Kalitlar serverdagi bilan bir xil: qurilma (pos, kds, printer, queue, waiter), 'branch', 'module.<bo'lim>',
// narx jadvalida yana 'pack.<qurilma>' — paket hajmi (masalan ofitsiant 5 talik).
import { getModule } from './modules';

/** Qurilma limitlari (tarifda va qo'shimchada) */
export const LIMIT_UNITS: Record<string, string> = {
  pos: 'kassa',
  kds: 'oshxona ekrani',
  printer: 'oshxona printeri',
  queue: 'mijoz ekrani',
  waiter: 'ofitsiant telefoni',
};

export interface BillingAddon {
  key: string;
  qty: number;
  price: number;
  total: number;
  /** Paket hajmi (1 — donalab) */
  pack?: number;
  /** Tarifdan ortiq dona soni (paketda — ichidagi dona) */
  units?: number;
}

const unit = (k: string) => LIMIT_UNITS[k] ?? k;
const moduleLabel = (k: string) => getModule(k)?.label ?? k;

/** Oylik summadagi qator: "2 ta ofitsiant telefoni paketi (5 talik)", "1 ta qo'shimcha filial", "Maslahatchi bo'limi" */
export function addonLabel(a: Pick<BillingAddon, 'key' | 'qty' | 'pack'>): string {
  if (a.key === 'branch') return `${a.qty} ta qo‘shimcha filial`;
  if (a.key.startsWith('module.')) return `${moduleLabel(a.key.slice(7))} bo‘limi`;
  if ((a.pack ?? 1) > 1) return `${a.qty} ta ${unit(a.key)} paketi (${a.pack} talik)`;
  return `${a.qty} ta qo‘shimcha ${unit(a.key)}`;
}

/** Narx jadvalidagi kalit nomi (konsol) */
export function addonPriceLabel(key: string): string {
  if (key === 'branch') return 'Qo‘shimcha filial';
  if (key.startsWith('module.')) return `${moduleLabel(key.slice(7))} bo‘limi`;
  if (key.startsWith('pack.')) return `${unit(key.slice(5))}: paket hajmi`;
  return `Qo‘shimcha ${unit(key)}`;
}

// ---------------------------------------------------------------------------
// Oylik summa (Q-081): konsolda oldindan ko'rish uchun — serverdagi app.billing_monthly bilan bir xil qoidalar
// (kafolatsiz). Haqiqiy summani doim server hisoblaydi.
// ---------------------------------------------------------------------------
export interface PlanPricing {
  monthly_price: number | null;
  limits: Readonly<Record<string, number>>;
  addon_prices?: Readonly<Record<string, number>>;
  modules: readonly string[];
}

export interface QuoteInput {
  /** Tarifdagidan ortiq qurilmalar soni (dona): { pos: 1, waiter: 5 } */
  extra?: Readonly<Record<string, number>>;
  /** Restoranda yoqilgan bo'limlar (tarifdagilari ham bo'lishi mumkin) */
  modules?: readonly string[];
  /** Faol filiallar soni (standart 1) */
  branches?: number;
  /** Imtiyozli — qo'shimchalar hisoblanmaydi */
  privileged?: boolean;
}

export interface Quote {
  base: number | null;
  items: BillingAddon[];
  monthly: number | null;
}

const DEVICE_KEYS = Object.keys(LIMIT_UNITS);

/** Paket hajmi (1 — donalab) */
export function addonPack(plan: Pick<PlanPricing, 'addon_prices'>, key: string): number {
  return Math.max(1, Math.trunc(plan.addon_prices?.[`pack.${key}`] ?? 1));
}

export function quoteMonthly(plan: PlanPricing, input: QuoteInput = {}): Quote {
  const items: BillingAddon[] = [];
  const prices = plan.addon_prices ?? {};
  if (!input.privileged) {
    for (const key of DEVICE_KEYS) {
      const extra = Math.max(0, Math.trunc(input.extra?.[key] ?? 0));
      if (extra <= 0) continue;
      const pack = addonPack(plan, key);
      const qty = Math.ceil(extra / pack);
      const price = prices[key] ?? 0;
      items.push({ key, qty, pack, units: extra, price, total: qty * price });
    }
    const branchPrice = prices.branch;
    const extraBranches = Math.max(0, (input.branches ?? 1) - 1);
    if (branchPrice !== undefined && extraBranches > 0) {
      items.push({ key: 'branch', qty: extraBranches, pack: 1, units: extraBranches, price: branchPrice, total: extraBranches * branchPrice });
    }
    for (const m of [...new Set(input.modules ?? [])].sort()) {
      if (plan.modules.includes(m)) continue;
      const price = prices[`module.${m}`];
      if (price === undefined) continue;
      items.push({ key: `module.${m}`, qty: 1, pack: 1, units: 1, price, total: price });
    }
  }
  const base = plan.monthly_price;
  return { base, items, monthly: base === null ? null : base + items.reduce((s, i) => s + i.total, 0) };
}

/** Bir necha oyga oldindan to'lov (chegirma bilan, 100 so'mgacha yaxlitlangan) — server bilan bir xil */
export function periodAmount(monthly: number, months: number, discountPct: number): number {
  return Math.round((monthly * months * (100 - discountPct)) / 100 / 100) * 100;
}

/** Tarifdan tashqari bo'lim narxi (yo'q bo'lsa — null: bepul yoqiladi) */
export function moduleAddonPrice(plan: Pick<PlanPricing, 'addon_prices' | 'modules'>, key: string): number | null {
  if (plan.modules.includes(key)) return null;
  return plan.addon_prices?.[`module.${key}`] ?? null;
}

/**
 * Tarif almashganda restoran limitlari (Q-081) — serverdagi platform_set_plan bilan bir xil:
 * yangi tarif limiti; ishlab turgan qurilmalar uzilmaydi (ortig'i — qo'shimcha); ataylab kamaytirilgani saqlanadi.
 * Natija — restoranga yoziladigan limitlar (tarifdagiga teng bo'lsa — yozilmaydi).
 */
export function planChangeLimits(
  oldPlanLimits: Readonly<Record<string, number>>,
  newPlanLimits: Readonly<Record<string, number>>,
  overrides: Readonly<Record<string, number>>,
  used: Readonly<Record<string, number>>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of DEVICE_KEYS) {
    const oldBase = oldPlanLimits[k] ?? 0;
    const newBase = newPlanLimits[k] ?? 0;
    const ov = overrides[k];
    let target = ov !== undefined && ov < oldBase ? Math.min(ov, newBase) : newBase;
    target = Math.max(target, used[k] ?? 0);
    if (target !== newBase) out[k] = target;
  }
  return out;
}

/** Tarifdan ortiq qurilmalar soni: limit − tarifdagi (manfiy bo'lmaydi) */
export function extraDevices(planLimits: Readonly<Record<string, number>>, limits: Readonly<Record<string, number>>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of DEVICE_KEYS) {
    const extra = (limits[k] ?? planLimits[k] ?? 0) - (planLimits[k] ?? 0);
    if (extra > 0) out[k] = extra;
  }
  return out;
}

/**
 * Tarif almashganda qoladigan bo'limlar: yangi tarifdagilar va yangi tarifda narxi bor qo'shimchalar
 * (bog'liqligi o'chsa — o'zi ham o'chadi). Qolganlari o'chiriladi.
 */
export function planChangeModules(
  enabled: readonly string[],
  plan: Pick<PlanPricing, 'modules' | 'addon_prices'>,
  requires: (key: string) => readonly string[],
): { kept: string[]; disabled: string[] } {
  let kept = enabled.filter((m) => plan.modules.includes(m) || plan.addon_prices?.[`module.${m}`] !== undefined);
  for (;;) {
    const next = kept.filter((m) => requires(m).every((r) => kept.includes(r)));
    if (next.length === kept.length) break;
    kept = next;
  }
  return { kept: [...kept].sort(), disabled: enabled.filter((m) => !kept.includes(m)).sort() };
}
